import logging from 'utils/logging';
import { MOMENT_TYPES, NARRATION_KEYS, HOST_STATUS } from '../hostStateSlice';
import {
  GAMESTATE_LOBBY,
  GAMESTATE_ROUND,
  GAMESTATE_ROUND_FINAL,
  GAMESTATE_ROUNDSTATE_WAITING,
  GAMESTATE_ROUNDSTATE_FINALROUND_CATEGORY_PREVIEW,
  GAMESTATE_ROUNDSTATE_FINALROUND_WAGER_INSTRUCTIONS,
} from '../../gamestate/constants';
import { advanceState } from '../../gamestate/GameState';
import { determineNextRound } from '../../gamestate/roundCompletionUtils';
import { registerCacheReset } from '../momentCacheManager';

const log = logging.getLogger('moments.FinalCategoryReveal');
log.setLevel('info');

let triggered = false;

export const resetFinalCategoryRevealCache = () => {
  triggered = false;
};
registerCacheReset('round', resetFinalCategoryRevealCache);

/**
 * Trigger: fires during inter-round lobby when next round is final.
 * Generates 3 narrations early:
 *   1. FINAL_CATEGORY_BUILDUP — "Your final category is..." (LLM)
 *   2. FINAL_CATEGORY_REVEAL — Just the category name (no LLM)
 *   3. FINAL_WAGER_INSTRUCTIONS — "Enter your wagers..." (LLM)
 */
export const FinalCategoryRevealTrigger = {
  id: 'FINAL_CATEGORY_REVEAL',
  shouldTrigger: ({ currentState, questions, gameObject }) => {
    if (triggered) return false;

    if (currentState?.currentState !== GAMESTATE_LOBBY) return false;
    if (!currentState?.round) return false;

    const nextRound = determineNextRound(currentState.round, gameObject?.categories);
    if (nextRound !== GAMESTATE_ROUND_FINAL) return false;

    const finalQuestionId = gameObject?.categories?.final?.question;
    if (!finalQuestionId) return false;

    const question = questions?.find(q => q.id === finalQuestionId);
    if (!question) return false;

    triggered = true;
    log.info(`Final category reveal triggered (early generation): ${question.category}`);

    return {
      shouldTriggerAction: false,
      shouldGenerateNarration: true,
      multipleNarrations: [
        {
          narrationKey: NARRATION_KEYS.FINAL_CATEGORY_BUILDUP,
          momentType: MOMENT_TYPES.FINAL_CATEGORY_BUILDUP,
          context: { category: question.category },
        },
        {
          narrationKey: NARRATION_KEYS.FINAL_CATEGORY_REVEAL,
          momentType: MOMENT_TYPES.FINAL_CATEGORY_REVEAL,
          context: { category: question.category },
        },
        {
          narrationKey: NARRATION_KEYS.FINAL_WAGER_INSTRUCTIONS,
          momentType: MOMENT_TYPES.FINAL_WAGER_INSTRUCTIONS,
          context: { category: question.category },
        },
      ],
    };
  },
};

/**
 * Playback 1 (Buildup): "Your final category is..."
 * Plays during ROUND/final/waiting state.
 * onComplete: plays ding sound + advances to final.preview (reveals category on screen)
 */
export const FinalCategoryBuildupPlayback = {
  id: 'FINAL_CATEGORY_BUILDUP_PLAYBACK',
  momentType: MOMENT_TYPES.FINAL_CATEGORY_BUILDUP,

  shouldPlay: (gameState, hostState, narration, playedNarrations) => {
    if (!narration || narration.momentType !== MOMENT_TYPES.FINAL_CATEGORY_BUILDUP) return false;
    if (playedNarrations.has(narration.key)) return false;

    const inWaiting =
      gameState?.currentState === GAMESTATE_ROUND &&
      gameState?.round === GAMESTATE_ROUND_FINAL &&
      gameState?.roundState === GAMESTATE_ROUNDSTATE_WAITING;
    if (!inWaiting) return false;

    const ready = narration.textStatus === 'READY' && (narration.audioStatus === 'READY' || narration.audioStatus === 'ERROR');
    if (!ready) return false;

    if (hostState?.status === HOST_STATUS.SPEAKING) return false;

    return true;
  },

  onComplete: async (gameId, dispatch, getState) => {
    log.info('Category buildup complete, playing ding + advancing to final.preview');
    try {
      try {
        const ding = new Audio('/sounds/category_reveal_ding.wav');
        ding.volume = 0.7;
        await ding.play();
        await new Promise(resolve => setTimeout(resolve, 800));
      } catch (audioErr) {
        log.warn('Could not play ding sound:', audioErr);
      }

      const state = getState();
      const gameObject = state.game?.game;
      if (!gameObject) return;
      const players = Object.values(state.player?.entities || {});
      await advanceState(gameObject, players, dispatch);
    } catch (error) {
      log.error('Error advancing after category buildup:', error);
    }
  },
};

/**
 * Playback 2 (Category Name): Just "[Category]!"
 * Plays during final.preview state (category is now visible on screen).
 * onComplete: advances to final.wagerInstructions, which is the gate
 * for the next narration to play.
 */
export const FinalCategoryNamePlayback = {
  id: 'FINAL_CATEGORY_NAME_PLAYBACK',
  momentType: MOMENT_TYPES.FINAL_CATEGORY_REVEAL,

  shouldPlay: (gameState, hostState, narration, playedNarrations) => {
    if (!narration || narration.momentType !== MOMENT_TYPES.FINAL_CATEGORY_REVEAL) return false;
    if (playedNarrations.has(narration.key)) return false;

    const inPreview =
      gameState?.currentState === GAMESTATE_ROUND &&
      gameState?.round === GAMESTATE_ROUND_FINAL &&
      gameState?.roundState === GAMESTATE_ROUNDSTATE_FINALROUND_CATEGORY_PREVIEW;
    if (!inPreview) return false;

    const ready = narration.textStatus === 'READY' && (narration.audioStatus === 'READY' || narration.audioStatus === 'ERROR');
    if (!ready) return false;

    if (hostState?.status === HOST_STATUS.SPEAKING) return false;

    return true;
  },

  onComplete: async (gameId, dispatch, getState) => {
    log.info('Category name complete, advancing to final.wagerInstructions');
    try {
      const state = getState();
      const gameObject = state.game?.game;
      if (!gameObject) return;
      const players = Object.values(state.player?.entities || {});
      await advanceState(gameObject, players, dispatch);
    } catch (error) {
      log.error('Error advancing after category name:', error);
    }
  },
};

/**
 * Playback 3 (Wager Instructions): "Enter your wagers now..."
 * Plays during final.wagerInstructions state (category name already read).
 * onComplete: advances to question.wagering.
 */
export const FinalWagerInstructionsPlayback = {
  id: 'FINAL_WAGER_INSTRUCTIONS_PLAYBACK',
  momentType: MOMENT_TYPES.FINAL_WAGER_INSTRUCTIONS,

  shouldPlay: (gameState, hostState, narration, playedNarrations) => {
    if (!narration || narration.momentType !== MOMENT_TYPES.FINAL_WAGER_INSTRUCTIONS) return false;
    if (playedNarrations.has(narration.key)) return false;

    const inWagerInstructions =
      gameState?.currentState === GAMESTATE_ROUND &&
      gameState?.round === GAMESTATE_ROUND_FINAL &&
      gameState?.roundState === GAMESTATE_ROUNDSTATE_FINALROUND_WAGER_INSTRUCTIONS;
    if (!inWagerInstructions) return false;

    const ready = narration.textStatus === 'READY' && (narration.audioStatus === 'READY' || narration.audioStatus === 'ERROR');
    if (!ready) return false;

    if (hostState?.status === HOST_STATUS.SPEAKING) return false;

    return true;
  },

  onComplete: async (gameId, dispatch, getState) => {
    log.info('Wager instructions complete, advancing to wagering');
    try {
      const state = getState();
      const gameObject = state.game?.game;
      if (!gameObject) return;
      const players = Object.values(state.player?.entities || {});
      await advanceState(gameObject, players, dispatch);
    } catch (error) {
      log.error('Error advancing after wager instructions:', error);
    }
  },
};

const api = {
  trigger: FinalCategoryRevealTrigger,
  playback: [FinalCategoryBuildupPlayback, FinalCategoryNamePlayback, FinalWagerInstructionsPlayback],
};

export default api;
